// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 — see ADDITIONAL-TERMS.md.

import './styles/main.css';
import { bindDrawer, clearLog, emit, logText, onEvent } from './eventlog.ts';
import { GLOSSARY } from './glossary.ts';
import { drawFrame } from './render.ts';
import { dimensionsFor, resolvePalette } from './theme.ts';
import type {
  Aspect,
  PaletteId,
  RenderRequest,
  RenderSettings,
  WaveStyle,
  WorkerOut,
} from './types.ts';
import { buildShell, escapeHtml, MODAL_CONTENT, type Refs } from './ui.ts';
import { computePeakEnvelope, computeSpectrumBars, mixToMono, smoothBars } from './waveform.ts';
import { formatBytes, formatDuration } from './format.ts';

const SETTINGS_KEY = 'wavewell.settings.v1';
const SAMPLE_URL = 'samples/wavewell-sample.wav';
const FFT_SIZE = 2048;

interface Decoded {
  channels: Float32Array[];
  mono: Float32Array;
  sampleRate: number;
  duration: number;
  name: string;
}

let refs: Refs;
let audioCtx: AudioContext | null = null;
let decoded: Decoded | null = null;
let bg: { blob: Blob; bitmap: ImageBitmap } | null = null;
let settings: RenderSettings = defaultSettings();

// preview playback
let sourceNode: AudioBufferSourceNode | null = null;
let previewBuffer: AudioBuffer | null = null;
let playing = false;
let pausedTime = 0;
let playStartCtx = 0;
let previewRaf = 0;
let smoothed: Float32Array = new Float32Array(settings.barCount);
let envelopeCache: Float32Array | null = null;

// render
let worker: Worker | null = null;
let resultUrl: string | null = null;
let resultBlob: Blob | null = null;

function defaultSettings(): RenderSettings {
  return {
    style: 'bars',
    aspect: 'portrait',
    palette: 'studio',
    fps: 30,
    caption: '',
    showProgressBar: true,
    barCount: 64,
    sensitivity: 1.15,
  };
}

function loadSettings(): void {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return;
    const p = JSON.parse(raw) as Partial<RenderSettings>;
    settings = { ...settings, ...p, caption: '' }; // never persist caption text
  } catch {
    /* ignore */
  }
}

function saveSettings(): void {
  try {
    const { caption: _c, ...rest } = settings;
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(rest));
  } catch {
    /* ignore */
  }
}

function ensureAudioCtx(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext();
  return audioCtx;
}

// ───────────────────────── ingestion ─────────────────────────

async function handleFile(file: File): Promise<void> {
  emit('info', `Loading “${file.name}” (${formatBytes(file.size)}).`);
  try {
    const buf = await file.arrayBuffer();
    const ctx = ensureAudioCtx();
    if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
    const audioBuffer = await ctx.decodeAudioData(buf.slice(0));
    const channels: Float32Array[] = [];
    for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
      channels.push(audioBuffer.getChannelData(c).slice());
    }
    decoded = {
      channels,
      mono: mixToMono(channels),
      sampleRate: audioBuffer.sampleRate,
      duration: audioBuffer.duration,
      name: file.name,
    };
    previewBuffer = audioBuffer;
    emit('ok', `Decoded ${formatDuration(audioBuffer.duration)} · ${audioBuffer.numberOfChannels} ch · ${audioBuffer.sampleRate} Hz.`);
    envelopeCache = null;
    bg?.bitmap.close();
    bg = null;
    resetPreview();
    enterEditor();
  } catch (err) {
    emit('err', `Could not decode that file: ${err instanceof Error ? err.message : 'unsupported format'}.`);
    openInfo('Unsupported audio', `<p>That file couldn’t be decoded. Wavewell supports the audio formats your browser can read — usually MP3, M4A/AAC, WAV, OGG and FLAC.</p>`);
  }
}

async function loadSample(): Promise<void> {
  try {
    const res = await fetch(SAMPLE_URL);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const file = new File([blob], 'wavewell-sample.wav', { type: 'audio/wav' });
    if (!settings.caption) {
      settings.caption = 'Made with Wavewell';
      refs.captionInput.value = settings.caption;
    }
    await handleFile(file);
  } catch (err) {
    emit('err', `Sample failed to load: ${err instanceof Error ? err.message : 'error'}.`);
  }
}

// ───────────────────────── stages ─────────────────────────

function show(stage: 'idle' | 'editor' | 'result'): void {
  refs.stageIdle.hidden = stage !== 'idle';
  refs.stageEditor.hidden = stage !== 'editor';
  refs.stageResult.hidden = stage !== 'result';
}

function enterEditor(): void {
  if (!decoded) return;
  refs.fileName.textContent = decoded.name;
  applyAspect();
  syncControls();
  show('editor');
  startPreviewLoop();
  drawPreviewAt(pausedTime);
}

function backToIdle(): void {
  stopPlayback();
  stopPreviewLoop();
  show('idle');
}

// ───────────────────────── preview ─────────────────────────

function resetPreview(): void {
  stopPlayback();
  pausedTime = 0;
  smoothed = new Float32Array(settings.barCount);
  refs.scrub.value = '0';
}

function applyAspect(): void {
  const { width, height } = dimensionsFor(settings.aspect);
  refs.preview.width = width;
  refs.preview.height = height;
  refs.canvasHolder.dataset.aspect = settings.aspect;
  envelopeCache = null;
}

function ensureEnvelope(): Float32Array {
  if (!decoded) return new Float32Array(0);
  if (!envelopeCache || settings.style === 'wave') {
    const innerW = dimensionsFor(settings.aspect).width * 0.84;
    envelopeCache = computePeakEnvelope(
      decoded.mono,
      Math.max(24, Math.floor(innerW / 3)),
      settings.sensitivity,
    );
  }
  return envelopeCache;
}

function drawPreviewAt(time: number): void {
  if (!decoded) return;
  const ctx = refs.preview.getContext('2d');
  if (!ctx) return;
  const { width, height } = dimensionsFor(settings.aspect);
  const progress = decoded.duration > 0 ? Math.min(1, time / decoded.duration) : 0;

  let bars: Float32Array | null = null;
  let envelope: Float32Array | null = null;
  if (settings.style === 'wave') {
    envelope = ensureEnvelope();
  } else {
    const raw = computeSpectrumBars(
      decoded.mono,
      decoded.sampleRate,
      Math.floor(time * decoded.sampleRate),
      FFT_SIZE,
      settings.barCount,
      settings.sensitivity,
    );
    if (playing) {
      smoothed = smoothBars(smoothed, raw, 0.6, 0.12);
      bars = smoothed;
    } else {
      bars = raw;
    }
  }

  drawFrame(ctx, {
    width,
    height,
    palette: resolvePalette(settings.palette),
    style: settings.style,
    bars,
    envelope,
    progress,
    caption: settings.caption,
    showProgressBar: settings.showProgressBar,
    background: bg?.bitmap ?? null,
  });

  refs.scrub.value = String(Math.round(progress * 1000));
  refs.timeLabel.textContent = `${formatDuration(time)} / ${formatDuration(decoded.duration)}`;
}

function currentTime(): number {
  if (!decoded) return 0;
  if (playing) {
    const t = pausedTime + (ensureAudioCtx().currentTime - playStartCtx);
    return Math.min(decoded.duration, Math.max(0, t));
  }
  return pausedTime;
}

function previewTick(): void {
  drawPreviewAt(currentTime());
  if (playing && currentTime() >= decoded!.duration - 1e-3) {
    stopPlayback();
    pausedTime = decoded!.duration;
  }
  previewRaf = requestAnimationFrame(previewTick);
}

function startPreviewLoop(): void {
  if (previewRaf) return;
  previewRaf = requestAnimationFrame(previewTick);
}
function stopPreviewLoop(): void {
  if (previewRaf) cancelAnimationFrame(previewRaf);
  previewRaf = 0;
}

function startPlayback(): void {
  if (!decoded || !previewBuffer || playing) return;
  const ctx = ensureAudioCtx();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  if (pausedTime >= decoded.duration - 1e-3) pausedTime = 0;
  const node = ctx.createBufferSource();
  node.buffer = previewBuffer;
  node.connect(ctx.destination);
  node.onended = () => {
    /* handled by tick / stopPlayback */
  };
  node.start(0, pausedTime);
  sourceNode = node;
  playStartCtx = ctx.currentTime;
  playing = true;
  smoothed = new Float32Array(settings.barCount);
  setPlayIcon(true);
}

function stopPlayback(): void {
  if (sourceNode) {
    pausedTime = currentTime();
    try {
      sourceNode.onended = null;
      sourceNode.stop();
    } catch {
      /* already stopped */
    }
    sourceNode.disconnect();
    sourceNode = null;
  }
  playing = false;
  setPlayIcon(false);
}

function togglePlay(): void {
  if (!decoded) return;
  if (playing) stopPlayback();
  else startPlayback();
}

function setPlayIcon(isPlaying: boolean): void {
  refs.playBtn.querySelector<HTMLElement>('.i-play')!.hidden = isPlaying;
  refs.playBtn.querySelector<HTMLElement>('.i-pause')!.hidden = !isPlaying;
  refs.playBtn.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
}

function seekTo(ratio: number): void {
  if (!decoded) return;
  const wasPlaying = playing;
  stopPlayback();
  pausedTime = Math.min(decoded.duration, Math.max(0, ratio * decoded.duration));
  smoothed = new Float32Array(settings.barCount);
  drawPreviewAt(pausedTime);
  if (wasPlaying) startPlayback();
}

// ───────────────────────── controls ─────────────────────────

function syncControls(): void {
  setSeg('style', settings.style);
  setSeg('aspect', settings.aspect);
  setSwatch(settings.palette);
  refs.captionInput.value = settings.caption;
  refs.sensitivity.value = String(settings.sensitivity);
  refs.progressToggle.checked = settings.showProgressBar;
  refs.bgClearBtn.hidden = !bg;
}

function setSeg(name: string, value: string): void {
  refs.root.querySelectorAll<HTMLButtonElement>(`[data-seg="${name}"] .seg-btn`).forEach((b) => {
    b.classList.toggle('active', b.dataset.value === value);
  });
}
function setSwatch(value: string): void {
  refs.root.querySelectorAll<HTMLButtonElement>('[data-seg="palette"] .swatch').forEach((b) => {
    b.classList.toggle('active', b.dataset.value === value);
  });
}

function wireControls(): void {
  refs.root.querySelector('[data-seg="style"]')!.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.seg-btn');
    if (!btn) return;
    settings.style = btn.dataset.value as WaveStyle;
    setSeg('style', settings.style);
    saveSettings();
    smoothed = new Float32Array(settings.barCount);
    drawPreviewAt(currentTime());
  });
  refs.root.querySelector('[data-seg="aspect"]')!.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.seg-btn');
    if (!btn) return;
    settings.aspect = btn.dataset.value as Aspect;
    setSeg('aspect', settings.aspect);
    saveSettings();
    applyAspect();
    drawPreviewAt(currentTime());
  });
  refs.root.querySelector('[data-seg="palette"]')!.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.swatch');
    if (!btn) return;
    settings.palette = btn.dataset.value as PaletteId;
    setSwatch(settings.palette);
    saveSettings();
    drawPreviewAt(currentTime());
  });
  refs.captionInput.addEventListener('input', () => {
    settings.caption = refs.captionInput.value;
    drawPreviewAt(currentTime());
  });
  refs.sensitivity.addEventListener('input', () => {
    settings.sensitivity = Number(refs.sensitivity.value);
    envelopeCache = null;
    saveSettings();
    drawPreviewAt(currentTime());
  });
  refs.progressToggle.addEventListener('change', () => {
    settings.showProgressBar = refs.progressToggle.checked;
    saveSettings();
    drawPreviewAt(currentTime());
  });

  refs.playBtn.addEventListener('click', togglePlay);
  refs.scrub.addEventListener('input', () => seekTo(Number(refs.scrub.value) / 1000));
  refs.changeFile.addEventListener('click', backToIdle);

  // background image
  refs.bgPickBtn.addEventListener('click', () => refs.bgInput.click());
  refs.bgInput.addEventListener('change', async () => {
    const f = refs.bgInput.files?.[0];
    refs.bgInput.value = '';
    if (!f) return;
    try {
      const bitmap = await createImageBitmap(f);
      bg?.bitmap.close();
      bg = { blob: f, bitmap };
      refs.bgClearBtn.hidden = false;
      emit('info', `Background image added (${formatBytes(f.size)}).`);
      drawPreviewAt(currentTime());
    } catch {
      emit('warn', 'That image could not be loaded.');
    }
  });
  refs.bgClearBtn.addEventListener('click', () => {
    bg?.bitmap.close();
    bg = null;
    refs.bgClearBtn.hidden = true;
    drawPreviewAt(currentTime());
  });

  refs.renderBtn.addEventListener('click', () => void startRender());
}

// ───────────────────────── render ─────────────────────────

async function startRender(): Promise<void> {
  if (!decoded) return;
  stopPlayback();
  showRenderOverlay(true);
  setRenderProgress('render', 0, 'Preparing…');

  worker?.terminate();
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

  const channels = decoded.channels.map((c) => c.slice()); // fresh copies to transfer
  const request: RenderRequest = {
    channels,
    sampleRate: decoded.sampleRate,
    durationSec: decoded.duration,
    settings: { ...settings },
    backgroundImage: bg?.blob ?? null,
  };

  worker.onmessage = (e: MessageEvent<WorkerOut>) => handleWorkerMessage(e.data);
  worker.onerror = (e) => {
    emit('err', `Worker error: ${e.message}`);
    showRenderOverlay(false);
    openInfo('Render failed', `<p>Something went wrong while encoding. ${escapeHtml(e.message)}</p>`);
  };
  emit('info', `Rendering ${settings.style} · ${settings.aspect} · ${settings.palette}.`);
  worker.postMessage(
    { type: 'render', request },
    channels.map((c) => c.buffer),
  );
}

function handleWorkerMessage(msg: WorkerOut): void {
  switch (msg.type) {
    case 'progress': {
      const label = msg.stage === 'render' ? 'Rendering frames…' : msg.stage === 'audio' ? 'Encoding audio…' : 'Finishing up…';
      const overall = msg.stage === 'render' ? msg.ratio * 0.85 : msg.stage === 'audio' ? 0.85 + msg.ratio * 0.12 : 0.99;
      setRenderProgress(label, overall, msg.note ?? '');
      break;
    }
    case 'log':
      emit(msg.level === 'error' ? 'err' : msg.level === 'warn' ? 'warn' : 'info', msg.message);
      break;
    case 'done':
      finishRender(msg.buffer, msg.mime, msg.hasAudio);
      break;
    case 'error':
      showRenderOverlay(false);
      if (msg.code === 'cancelled') {
        emit('warn', 'Render cancelled.');
      } else if (msg.code === 'no-avc') {
        emit('err', msg.message);
        openInfo('Video encoding unavailable', `<p>${escapeHtml(msg.message)}</p><p class="muted">WebCodecs H.264 encoding is available in recent Chrome, Edge and Safari.</p>`);
      } else {
        emit('err', msg.message);
        openInfo('Render failed', `<p>${escapeHtml(msg.message)}</p>`);
      }
      worker?.terminate();
      worker = null;
      break;
  }
}

function finishRender(buffer: ArrayBuffer, mime: string, hasAudio: boolean): void {
  worker?.terminate();
  worker = null;
  if (resultUrl) URL.revokeObjectURL(resultUrl);
  resultBlob = new Blob([buffer], { type: mime });
  resultUrl = URL.createObjectURL(resultBlob);
  refs.resultVideo.src = resultUrl;
  const { width, height } = dimensionsFor(settings.aspect);
  refs.resultMeta.textContent = `${formatBytes(resultBlob.size)} · ${width}×${height} · ${formatDuration(decoded?.duration ?? 0)}${hasAudio ? '' : ' · no audio'}`;
  refs.shareBtn.hidden = !canShareFile();
  showRenderOverlay(false);
  show('result');
  stopPreviewLoop();
  emit('ok', `Video ready — ${formatBytes(resultBlob.size)}.`);
}

function outputName(): string {
  const base = (decoded?.name ?? 'audio').replace(/\.[^.]+$/, '') || 'wavewell';
  return `${base}-wavewell.mp4`;
}

function canShareFile(): boolean {
  if (!('canShare' in navigator) || !resultBlob) return false;
  try {
    const f = new File([resultBlob], outputName(), { type: 'video/mp4' });
    return navigator.canShare({ files: [f] });
  } catch {
    return false;
  }
}

async function downloadResult(): Promise<void> {
  if (!resultBlob) return;
  const name = outputName();
  const anySave = window as unknown as {
    showSaveFilePicker?: (o: unknown) => Promise<{ createWritable: () => Promise<{ write: (b: Blob) => Promise<void>; close: () => Promise<void> }> }>;
  };
  if (anySave.showSaveFilePicker) {
    try {
      const handle = await anySave.showSaveFilePicker({
        suggestedName: name,
        types: [{ description: 'MP4 video', accept: { 'video/mp4': ['.mp4'] } }],
      });
      const w = await handle.createWritable();
      await w.write(resultBlob);
      await w.close();
      emit('ok', 'Saved.');
      return;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // fall through to anchor download
    }
  }
  const a = document.createElement('a');
  a.href = resultUrl!;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  emit('ok', 'Download started.');
}

async function shareResult(): Promise<void> {
  if (!resultBlob) return;
  try {
    const f = new File([resultBlob], outputName(), { type: 'video/mp4' });
    await navigator.share({ files: [f], title: 'Wavewell video' });
    emit('ok', 'Shared.');
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    emit('warn', 'Share was not completed.');
  }
}

async function copyResult(): Promise<void> {
  if (!resultBlob) return;
  try {
    const item = new ClipboardItem({ 'video/mp4': resultBlob });
    await navigator.clipboard.write([item]);
    emit('ok', 'Copied to clipboard.');
  } catch {
    emit('warn', 'This browser won’t copy a video to the clipboard — use Download instead.');
    openInfo('Copy not supported', '<p>Your browser can’t place a video on the clipboard. Use <strong>Download MP4</strong> or <strong>Share</strong> instead.</p>');
  }
}

// ───────────────────────── overlays ─────────────────────────

function showRenderOverlay(on: boolean): void {
  refs.renderOverlay.hidden = !on;
}
function setRenderProgress(label: string, ratio: number, note: string): void {
  refs.renderStageLabel.textContent = label;
  refs.renderBar.style.width = `${Math.round(Math.min(1, Math.max(0, ratio)) * 100)}%`;
  refs.renderNote.textContent = note;
}
function cancelRender(): void {
  worker?.postMessage({ type: 'cancel' });
  setTimeout(() => {
    worker?.terminate();
    worker = null;
    showRenderOverlay(false);
  }, 120);
}

// ───────────────────────── modals & tooltip ─────────────────────────

function openModal(key: string): void {
  const c = MODAL_CONTENT[key];
  if (!c) return;
  openInfo(c.title, c.body);
}

function openInfo(title: string, bodyHtml: string): void {
  refs.modalHost.innerHTML = `
    <div class="modal-backdrop"></div>
    <div class="modal" role="dialog" aria-modal="true" aria-label="${escapeHtml(title.replace(/&amp;/g, '&'))}">
      <div class="modal-head">
        <h3>${title}</h3>
        <button class="icon-btn" id="modal-close" aria-label="Close">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>
      <div class="modal-body">${bodyHtml}</div>
    </div>`;
  refs.modalHost.hidden = false;
  refs.modalHost.querySelector('#modal-close')!.addEventListener('click', closeModal);
  refs.modalHost.querySelector('.modal-backdrop')!.addEventListener('click', closeModal);
}
function closeModal(): void {
  refs.modalHost.hidden = true;
  refs.modalHost.innerHTML = '';
}

function showTooltip(target: HTMLElement, term: string): void {
  const def = GLOSSARY[term];
  if (!def) return;
  const tip = refs.tooltip;
  tip.textContent = def;
  tip.hidden = false;
  const r = target.getBoundingClientRect();
  const tr = tip.getBoundingClientRect();
  let left = r.left + r.width / 2 - tr.width / 2;
  left = Math.max(10, Math.min(window.innerWidth - tr.width - 10, left));
  let top = r.top - tr.height - 10;
  if (top < 10) top = r.bottom + 10;
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}
function hideTooltip(): void {
  refs.tooltip.hidden = true;
}

// ───────────────────────── drawer ─────────────────────────

function toggleDrawer(force?: boolean): void {
  const open = force ?? refs.drawer.hidden;
  refs.drawer.hidden = !open;
}

// ───────────────────────── dropzone ─────────────────────────

function wireDropzone(): void {
  const dz = refs.dropzone;
  refs.pickBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    refs.fileInput.click();
  });
  refs.sampleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    void loadSample();
  });
  dz.addEventListener('click', () => refs.fileInput.click());
  dz.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      refs.fileInput.click();
    }
  });
  refs.fileInput.addEventListener('change', () => {
    const f = refs.fileInput.files?.[0];
    refs.fileInput.value = '';
    if (f) void handleFile(f);
  });
  ['dragenter', 'dragover'].forEach((ev) =>
    dz.addEventListener(ev, (e) => {
      e.preventDefault();
      dz.classList.add('drag');
    }),
  );
  ['dragleave', 'drop'].forEach((ev) =>
    dz.addEventListener(ev, (e) => {
      e.preventDefault();
      if (ev === 'dragleave' && dz.contains((e as DragEvent).relatedTarget as Node)) return;
      dz.classList.remove('drag');
    }),
  );
  dz.addEventListener('drop', (e) => {
    const f = (e as DragEvent).dataTransfer?.files?.[0];
    if (f) void handleFile(f);
  });
  // window-wide drop while idle
  window.addEventListener('dragover', (e) => {
    if (!refs.stageIdle.hidden) e.preventDefault();
  });
  window.addEventListener('drop', (e) => {
    if (refs.stageIdle.hidden) return;
    e.preventDefault();
    const f = (e as DragEvent).dataTransfer?.files?.[0];
    if (f && (f.type.startsWith('audio/') || /\.(mp3|m4a|wav|ogg|flac|aac)$/i.test(f.name))) void handleFile(f);
  });
}

// ───────────────────────── global wiring ─────────────────────────

function wireGlobal(): void {
  // nav modals
  refs.root.querySelectorAll<HTMLButtonElement>('[data-modal]').forEach((b) =>
    b.addEventListener('click', () => openModal(b.dataset.modal!)),
  );
  refs.root.querySelector('#trust-badge')!.addEventListener('click', () => openModal('privacy'));

  // drawer
  refs.drawerToggle.addEventListener('click', () => toggleDrawer());
  refs.drawerClose.addEventListener('click', () => toggleDrawer(false));
  refs.drawerCopy.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(logText());
      emit('ok', 'Log copied.');
    } catch {
      emit('warn', 'Clipboard blocked.');
    }
  });
  refs.drawerClear.addEventListener('click', () => clearLog());

  // result actions
  refs.downloadBtn.addEventListener('click', () => void downloadResult());
  refs.shareBtn.addEventListener('click', () => void shareResult());
  refs.copyBtn.addEventListener('click', () => void copyResult());
  refs.againBtn.addEventListener('click', () => {
    show('editor');
    startPreviewLoop();
    drawPreviewAt(currentTime());
  });
  refs.cancelRender.addEventListener('click', cancelRender);

  // glossary tooltips (event-delegated, works for lazily-injected modal content)
  document.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest<HTMLElement>('.glossary-link');
    if (link) {
      e.stopPropagation();
      if (!refs.tooltip.hidden) hideTooltip();
      else showTooltip(link, link.dataset.term!);
      return;
    }
    if (!refs.tooltip.hidden) hideTooltip();
  });
  window.addEventListener('scroll', hideTooltip, true);
  window.addEventListener('resize', hideTooltip);

  // keyboard
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (!refs.tooltip.hidden) return hideTooltip();
      if (!refs.modalHost.hidden) return closeModal();
      if (!refs.renderOverlay.hidden) return cancelRender();
      if (!refs.drawer.hidden) return toggleDrawer(false);
    }
    const typing = (e.target as HTMLElement)?.tagName === 'TEXTAREA' || (e.target as HTMLElement)?.tagName === 'INPUT';
    if (typing) return;
    if (e.key === ' ' && !refs.stageEditor.hidden) {
      e.preventDefault();
      togglePlay();
    }
    if (e.key === 'Enter' && !refs.stageEditor.hidden && refs.modalHost.hidden && refs.renderOverlay.hidden) {
      e.preventDefault();
      void startRender();
    }
  });

  window.addEventListener('pagehide', () => stopPlayback());
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && playing) stopPlayback();
  });
}

// ───────────────────────── boot ─────────────────────────

function boot(): void {
  const root = document.getElementById('app');
  if (!root) return;
  loadSettings();
  refs = buildShell(root);
  bindDrawer(refs.drawerList, refs.drawerCount);
  onEvent(() => {});
  wireDropzone();
  wireControls();
  wireGlobal();
  syncControls();
  emit('info', 'Wavewell ready. Everything runs on your device.');

  // Test hook: drive the pipeline with synthetic samples in automation.
  (window as unknown as { __wavewell?: unknown }).__wavewell = {
    loadFile: (f: File) => handleFile(f),
    getState: () => ({
      hasAudio: !!decoded,
      duration: decoded?.duration ?? 0,
      settings: { ...settings },
      resultBytes: resultBlob?.size ?? 0,
    }),
    render: () => startRender(),
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
